/**
 * VAT.
 *
 * Shelf prices in the shop INCLUDE VAT (16% in Palestine). So a ₪100 shirt is
 * ₪86.21 of goods plus ₪13.79 of VAT — not ₪100 plus ₪16.
 *
 * Every sale stores the VAT rate that was in force at the time, so changing the
 * rate later never rewrites history.
 */
import { type Minor, assertMinor, roundHalfUp } from './money.js';

export const DEFAULT_VAT_RATE = 16;

export interface VatSplit {
  /** What the customer pays, VAT included. */
  gross: Minor;
  /** The goods, before VAT. */
  net: Minor;
  /** The VAT inside the gross amount. */
  vat: Minor;
  rate: number;
}

/** Split a VAT-inclusive price into net + VAT. net + vat === gross, always. */
export function splitInclusive(gross: Minor, rate: number = DEFAULT_VAT_RATE): VatSplit {
  assertMinor(gross);
  if (!Number.isFinite(rate) || rate < 0) throw new Error('VAT rate must be zero or more');
  const net = roundHalfUp(gross / (1 + rate / 100));
  return { gross, net, vat: gross - net, rate };
}

/** Add VAT to a price that excludes it — used for supplier invoices quoted net. */
export function addExclusive(net: Minor, rate: number = DEFAULT_VAT_RATE): VatSplit {
  assertMinor(net);
  const vat = roundHalfUp((net * rate) / 100);
  return { gross: net + vat, net, vat, rate };
}

/**
 * VAT for a whole sale. Computed on the SALE TOTAL, not by adding up per-line VAT,
 * so the printed VAT figure always matches total - net exactly with no rounding drift.
 */
export function vatForSale(totalGross: Minor, rate: number = DEFAULT_VAT_RATE): VatSplit {
  return splitInclusive(totalGross, rate);
}
